/**
 * Messagerie du back office avec les CLIENTS (migration 0067).
 *
 *  • fil d'assistance client ↔ équipe : `client_messages`, une conversation par client, une course en contexte ou non.
 *    Le livreur ne le lit jamais (RLS) ;
 *  • fil d'une course client ↔ livreur : `course_messages` (0047), où l'équipe peut désormais écrire (« equipe »),
 *    pendant la course seulement.
 *
 * La BASE juge tout (qui lit, qui écrit, quelle course peut être citée) ; ce module ne fait que transporter et mettre en
 * forme. Les erreurs de la base sont traduites en phrases (`phraseMessagerie`).
 */
import { supabase } from './supabase'
import { lienWhatsApp, normaliserNumero } from './whatsapp'

export type AuteurClient = 'client' | 'equipe'
export type AuteurCourse = 'client' | 'livreur' | 'equipe'

export interface MessageClient {
  id: string
  client_account_id: string
  course_id: string | null
  auteur: AuteurClient
  source: 'app' | 'console' | 'api' | 'whatsapp'
  corps: string
  lu_le: string | null
  created_at: string
}

export interface MessageCourse {
  id: string
  course_id: string
  auteur: AuteurCourse
  corps: string
  lu_le: string | null
  created_at: string
}

/** Une ligne de la liste des conversations (`messagerie_clients`). */
export interface ConversationClient {
  client_account_id: string
  nom: string
  type: 'pme' | 'particulier'
  telephone: string | null
  a_un_compte: boolean
  dernier_corps: string
  dernier_auteur: AuteurClient
  dernier_le: string
  dernier_course_ref: string | null
  non_lus: number
  total: number
}

/** Données fictives de l'aperçu de développement (`?apercu`), jamais en production. */
export interface FixturesMessagerie {
  messagesClients: MessageClient[]
  messagesCourses: MessageCourse[]
}

const MESSAGES: Record<string, string> = {
  course_d_un_autre_client: 'Cette course n’est pas une course de ce client.',
  client_introuvable: 'Client introuvable.',
  messagerie_fermee: 'Le fil de la course est fermé : il n’est ouvert que pendant la course.',
  course_introuvable: 'Cette course n’a pas de fil client ↔ livreur (seules les courses de l’app en ont un).',
  introuvable: 'Conversation introuvable.',
  forbidden: 'Votre rôle ne donne pas accès à la messagerie des clients.',
}

/** La phrase à montrer pour une erreur de la base. */
export function phraseMessagerie(e: unknown): string {
  const m = e instanceof Error ? e.message : typeof e === 'object' && e && 'message' in e ? String((e as { message: unknown }).message) : String(e)
  for (const [code, phrase] of Object.entries(MESSAGES)) if (m.includes(code)) return phrase
  if (/row-level security|permission denied/i.test(m)) return 'La base a refusé ce message (droits).'
  return m || 'Envoi impossible.'
}

/* ------------------------------------------------------------------ lectures */

/** La liste des conversations ; vide (jamais en erreur) si la migration 0067 n'est pas encore en place ou sans droit. */
export async function lireConversationsClients(orgId: string): Promise<ConversationClient[]> {
  const { data, error } = await supabase.rpc('messagerie_clients', { p_org: orgId })
  if (error) return []
  return (data ?? []) as ConversationClient[]
}

export async function lireFilClient(clientId: string): Promise<MessageClient[]> {
  const { data, error } = await supabase
    .from('client_messages')
    .select('id,client_account_id,course_id,auteur,source,corps,lu_le,created_at')
    .eq('client_account_id', clientId)
    .order('created_at', { ascending: true })
    .limit(500)
  if (error) throw new Error(phraseMessagerie(error))
  return (data ?? []) as MessageClient[]
}

/** Les fils client ↔ livreur des courses données (courses en cours de l'app). */
export async function lireFilsCourses(courseIds: string[]): Promise<MessageCourse[]> {
  if (courseIds.length === 0) return []
  const { data, error } = await supabase
    .from('course_messages')
    .select('id,course_id,auteur,corps,lu_le,created_at')
    .in('course_id', courseIds)
    .order('created_at', { ascending: true })
    .limit(1000)
  if (error) return []
  return (data ?? []) as MessageCourse[]
}

/* ------------------------------------------------------------------ écritures */

export async function envoyerMessageClient(clientId: string, texte: string, courseId: string | null): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  const { error } = await supabase.from('client_messages').insert({
    client_account_id: clientId,
    course_id: courseId,
    auteur: 'equipe',
    auteur_profile_id: user?.id ?? null,
    corps: texte,
  })
  if (error) throw new Error(phraseMessagerie(error))
}

export async function envoyerMessageCourse(courseId: string, texte: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  const { error } = await supabase.from('course_messages').insert({
    course_id: courseId,
    auteur: 'equipe',
    auteur_profile_id: user?.id ?? null,
    corps: texte,
  })
  if (error) throw new Error(phraseMessagerie(error))
}

export async function marquerFilClientLu(clientId: string): Promise<void> {
  const { error } = await supabase.rpc('marquer_messages_client_lus', { p_client: clientId })
  if (error) throw new Error(phraseMessagerie(error))
}

/* ------------------------------------------------------------------ temps réel */

/** Tout nouveau message d'un fil client (client ou équipe) et de fil de course de l'organisation arrive en direct. */
export function suivreMessagesClients(orgId: string, surMessage: () => void): () => void {
  const canal = supabase
    .channel('ops-messages-clients')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'client_messages', filter: `org_id=eq.${orgId}` }, () => surMessage())
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'course_messages', filter: `org_id=eq.${orgId}` }, () => surMessage())
    .subscribe()
  return () => {
    void supabase.removeChannel(canal)
  }
}

/* ------------------------------------------------------------------ mise en forme (pure, testée) */

/** Total des messages de clients non lus, pour la pastille de la navigation. */
export function totalNonLus(conversations: ConversationClient[]): number {
  return conversations.reduce((n, c) => n + (c.non_lus > 0 ? c.non_lus : 0), 0)
}

/** L'aperçu d'une conversation dans la liste : « Vous : … » quand l'équipe a écrit en dernier, tronqué. */
export function apercuDernier(c: Pick<ConversationClient, 'dernier_auteur' | 'dernier_corps'>, max = 70): string {
  const texte = c.dernier_corps.replace(/\s+/g, ' ').trim()
  const court = texte.length > max ? `${texte.slice(0, max - 1)}…` : texte
  return c.dernier_auteur === 'equipe' ? `Vous : ${court}` : court
}

/**
 * Les conversations à afficher : celles qui existent (la plus récente d'abord), puis, si une recherche est saisie, les
 * clients qui n'ont pas encore de conversation et dont le nom ou le téléphone correspond (pour en ouvrir une).
 */
export function conversationsAffichees<C extends { id: string; name: string; phone: string | null; type: 'pme' | 'particulier'; a_un_compte: boolean }>(
  conversations: ConversationClient[],
  clients: C[],
  recherche: string,
): { existantes: ConversationClient[]; nouvelles: C[] } {
  const q = normaliser(recherche)
  const qTel = q.replace(/\D/g, '')
  const correspond = (nom: string, tel: string | null) =>
    !q || normaliser(nom).includes(q) || (qTel.length >= 3 && (tel ?? '').replace(/\D/g, '').includes(qTel))
  const existantes = [...conversations]
    .sort((a, b) => b.dernier_le.localeCompare(a.dernier_le))
    .filter((c) => correspond(c.nom, c.telephone))
  if (!q) return { existantes, nouvelles: [] }
  const deja = new Set(conversations.map((c) => c.client_account_id))
  const nouvelles = clients.filter((c) => !deja.has(c.id) && correspond(c.name, c.phone)).slice(0, 8)
  return { existantes, nouvelles }
}

function normaliser(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
}

/** Lien WhatsApp vers le client (règles de numéro du module WhatsApp) ; nul si le numéro est inutilisable. */
export function whatsappClient(telephone: string | null, texte = ''): string | null {
  const numero = normaliserNumero(telephone)
  return numero ? lienWhatsApp(numero, texte) : null
}
